import { Link } from 'react-router-dom';
import { BookHeart, Sparkles } from 'lucide-react';
import { motion } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { useLanguage } from '@/i18n/LanguageContext';
import { useAnalytics } from '@/hooks/useAnalytics';
import { lc } from '@/i18n/copy';

const copy = {
  en: {
    title: 'Your Private Lifetime Reflection Space',
    body: 'TRESLOG is your private lifetime self-blog. Write about your life in the third person—gaining clarity and compassion. Never metered, never limited.',
    cta: 'Unlock with Legacy',
    badge: 'Private • Lifetime',
  },
  es: {
    title: 'Tu Espacio Privado de Reflexión de por Vida',
    body: 'TRESLOG es tu blog personal privado de por vida. Escribe sobre tu vida en tercera persona—ganando claridad y compasión. Nunca medido, nunca limitado.',
    cta: 'Desbloquear con Legado',
    badge: 'Privado • De por Vida',
  },
  fr: {
    title: 'Votre Espace Privé de Réflexion à Vie',
    body: 'TRESLOG est votre blog privé à vie. Écrivez sur votre vie à la troisième personne — gagnant en clarté et compassion. Jamais limité.',
    cta: 'Débloquer avec Legacy',
    badge: 'Privé • À Vie',
  },
  pt: {
    title: 'Seu Espaço Privado de Reflexão Vitalício',
    body: 'TRESLOG é seu blog pessoal privado vitalício. Escreva sobre sua vida na terceira pessoa — ganhando clareza e compaixão. Sem limites.',
    cta: 'Desbloquear com Legacy',
    badge: 'Privado • Vitalício',
  },
  ar: {
    title: 'مساحتك الخاصة للتأمل مدى الحياة',
    body: 'TRESLOG هو مدونتك الشخصية الخاصة مدى الحياة. اكتب عن حياتك بصيغة الغائب — لاكتساب الوضوح والتعاطف. بلا حدود.',
    cta: 'فتح مع Legacy',
    badge: 'خاص • مدى الحياة',
  },
  ja: {
    title: 'あなたのプライベートな生涯リフレクション空間',
    body: 'TRESLOGは生涯使えるプライベートなセルフブログです。第三者の視点で人生を書き、明晰さと思いやりを得ましょう。制限なし。',
    cta: 'Legacyで解放',
    badge: 'プライベート・生涯',
  },
  ko: {
    title: '당신의 프라이빗 평생 성찰 공간',
    body: 'TRESLOG는 평생 사용할 수 있는 프라이빗 셀프 블로그입니다. 3인칭으로 인생을 쓰며 명확함과 연민을 얻으세요. 제한 없음.',
    cta: 'Legacy로 잠금 해제',
    badge: '프라이빗 • 평생',
  },
  id: {
    title: 'Ruang Refleksi Pribadi Seumur Hidup Anda',
    body: 'TRESLOG adalah blog pribadi seumur hidup Anda. Tulis tentang hidup Anda dalam sudut pandang orang ketiga — dapatkan kejelasan dan kasih sayang. Tanpa batas.',
    cta: 'Buka dengan Legacy',
    badge: 'Pribadi • Seumur Hidup',
  },

  hi: {
    title: 'आपकी निजी आजीवन प्रतिबिंब जगह',
    body: 'TRESLOG आपका निजी आजीवन सेल्फ-ब्लॉग है। अपने जीवन के बारे में तीसरे व्यक्ति में लिखें—स्पष्टता और करुणा प्राप्त करें। कभी मापा नहीं गया, कभी सीमित नहीं।',
    cta: 'Legacy के साथ अनलॉक करें',
    badge: 'निजी • आजीवन',
  },
};

interface TreslogUpsellCardProps {
  className?: string;
}

export function TreslogUpsellCard({ className }: TreslogUpsellCardProps) {
  const { language } = useLanguage();
  const t = lc(copy, language);
  const { trackTreslogViewed, trackLegacyPlanViewed } = useAnalytics();

  const handleClick = () => {
    trackTreslogViewed('dashboard');
    trackLegacyPlanViewed('dashboard_upsell_card');
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.2 }}
      className={className}
    >
      <Card className="border-dashed border-2 border-amber-200 dark:border-amber-800/50 bg-gradient-to-br from-amber-50/50 to-orange-50/50 dark:from-amber-900/10 dark:to-orange-900/10">
        <CardContent className="flex flex-col items-center text-center py-8 px-6">
          <div className="mb-4 rounded-full bg-gradient-to-br from-amber-100 to-orange-100 dark:from-amber-900/40 dark:to-orange-900/40 p-4">
            <BookHeart className="h-8 w-8 text-amber-600 dark:text-amber-400" />
          </div>

          <div className="inline-flex items-center gap-1 mb-2">
            <Sparkles className="h-3 w-3 text-amber-500" />
            <span className="text-xs font-medium text-amber-600 dark:text-amber-400 uppercase tracking-wider">
              Legacy
            </span>
          </div>

          <h3 className="text-lg font-semibold text-foreground mb-2">{t.title}</h3>

          <p className="text-sm text-muted-foreground mb-6 max-w-xs leading-relaxed">
            {t.body}
          </p>

          <Link to="/#pricing" onClick={handleClick}>
            <Button
              variant="outline"
              size="sm"
              className="gap-2 border-amber-300 dark:border-amber-700 hover:bg-amber-50 dark:hover:bg-amber-900/20"
            >
              <BookHeart className="h-4 w-4" />
              {t.cta}
            </Button>
          </Link>
        </CardContent>
      </Card>
    </motion.div>
  );
}
